import { VerticalContainer, Wrapper } from '@american-autonomy/ag-ui';

import Banner from 'components/Banner';
import PanelSection from 'components/PanelSection';

import useTranslation from './hooks/useTranslation';

import AmsRecommendationSection from './components/AmsRecommendationSection';
import InteractionsSection from './components/InteractionsSection';
import PhWarningsSection from './components/PhWarningsSection';
import { SEVERITY_TO_BANNER_VARIANT, TEST_ID } from './constants';
import type { FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const CompatibilityAdvisoryPanel: FunctionComponent<Props> = ({
    amsRecommendation,
    className = '',
    hasError,
    interactions,
    isLoading,
    isUsingDefaultWaterProfile,
    phWarnings,
    testId = TEST_ID
}) => {
    const {
        AMS_TITLE,
        CITATION_LINK,
        CLEAN_MESSAGE,
        DEFAULT_WATER_PROFILE_NOTICE,
        ERROR_MESSAGE,
        LOADING,
        PH_TITLE,
        TITLE,
        amsDriver,
        amsRecommendationText,
        driverLabel,
        interactionsTitle,
        pairDescription,
        phAbove,
        phBelow
    } = useTranslation();

    if (hasError) {
        return (
            <VerticalContainer
                className={`${styles.compatibilityAdvisoryPanel} ${className}`}
                testId={testId}>
                <Banner icon='warning' text={ERROR_MESSAGE} variant='warn' />
            </VerticalContainer>
        );
    }

    if (isLoading) {
        return (
            <VerticalContainer
                className={`${styles.compatibilityAdvisoryPanel} ${className}`}
                testId={testId}>
                <Wrapper className={styles.cleanMessage}>{LOADING}</Wrapper>
            </VerticalContainer>
        );
    }

    const hasNoFindings =
        interactions.length === 0 && phWarnings.length === 0 && amsRecommendation === null;

    return (
        <VerticalContainer
            className={`${styles.compatibilityAdvisoryPanel} ${className}`}
            testId={testId}>
            <PanelSection text={TITLE}>
                {hasNoFindings && (
                    <Wrapper className={styles.cleanMessage}>{CLEAN_MESSAGE}</Wrapper>
                )}
            </PanelSection>

            {isUsingDefaultWaterProfile && (
                <Banner icon='info' text={DEFAULT_WATER_PROFILE_NOTICE} variant='info' />
            )}

            {interactions.length > 0 && (
                <InteractionsSection
                    describePair={pairDescription}
                    interactions={interactions}
                    severityToBannerVariant={SEVERITY_TO_BANNER_VARIANT}
                    title={interactionsTitle(interactions.length)}
                />
            )}

            {phWarnings.length > 0 && (
                <PhWarningsSection
                    describeAbove={phAbove}
                    describeBelow={phBelow}
                    phWarnings={phWarnings}
                    severityToBannerVariant={SEVERITY_TO_BANNER_VARIANT}
                    title={PH_TITLE(phWarnings.length)}
                />
            )}

            {amsRecommendation && (
                <AmsRecommendationSection
                    amsRecommendation={amsRecommendation}
                    citationLinkText={CITATION_LINK}
                    describeAmsDriver={amsDriver}
                    describeAmsRecommendation={amsRecommendationText}
                    driverLabel={driverLabel}
                    title={AMS_TITLE}
                />
            )}
        </VerticalContainer>
    );
};

export default CompatibilityAdvisoryPanel;
